import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Download, FileText, FileSpreadsheet } from "lucide-react";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function AdminReportsPage() {
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [busy, setBusy] = useState(false);

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 4; y--) years.push(String(y));

  const download = async (format) => {
    setBusy(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/report/monthly?year=${year}&month=${month}&format=${format}`;
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) throw new Error(`Report failed (${resp.status})`);
      const blob = await resp.blob();
      const a = document.createElement("a");
      const objurl = URL.createObjectURL(blob);
      a.href = objurl;
      a.download = `global_checks_report_${year}_${String(month).padStart(2, "0")}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objurl);
      toast.success(`Downloaded ${format.toUpperCase()} report`);
    } catch (err) {
      toast.error(err.message);
    } finally { setBusy(false); }
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-bold text-slate-900">Monthly reports</h1>
      <p className="text-slate-600 mt-1 text-sm">Export monthly case reports for internal reporting or client billing.</p>

      <div className="mt-8 border border-slate-200 rounded-2xl bg-white p-8 max-w-2xl">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Year</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger data-testid="report-year-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Month</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger data-testid="report-month-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button onClick={() => download("csv")} disabled={busy} data-testid="download-csv-btn"
            className="pill-btn bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-11">
            <FileSpreadsheet size={16} className="mr-2" /> Download CSV
          </Button>
          <Button onClick={() => download("pdf")} disabled={busy} data-testid="download-pdf-btn"
            variant="outline"
            className="pill-btn border-slate-300 text-slate-800 hover:bg-slate-900 hover:text-white hover:border-slate-900 h-11">
            <FileText size={16} className="mr-2" /> Download PDF
          </Button>
        </div>
        <p className="mt-4 text-xs text-slate-500">
          Reports include reference number, client, check type, candidate, country, status and timestamps.
        </p>
      </div>
    </div>
  );
}
